import { useMutation } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { taskQueryKeys } from '@omnea-app/fe-tasks/queryKeys'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { RequestDetailQueryKey, RequestDetailsQueryKey } from '../../../useRequestDetail'
import { useRequestComments } from '../comments/api/useRequestComments'
import { pauseDetailQueryKey } from '../paused-banner/useGetPauseDetail'

export const useResumeRequest = ({ requestId }: { requestId: string }) => {
  const { t } = useTranslation('requests')
  const { getToken } = useAuthenticatedUser()
  const { addResumeComment } = useRequestComments({ requestId })

  return useMutation({
    mutationFn: async () => {
      const apiClient = new RequestsHttpClient({ authorization: await getToken() })

      const { data, error } = await apiClient.POST('/{id}/resume', {
        params: { path: { id: requestId } },
        body: {},
      })

      throwIfApiError(error)

      return data
    },
    onSuccess: (_data, _variables, _, { client }) => {
      void addResumeComment()
      void client.invalidateQueries({ queryKey: [RequestDetailQueryKey, requestId] })
      void client.invalidateQueries({ queryKey: pauseDetailQueryKey(requestId) })
      void client.invalidateQueries({ queryKey: ['dashboard-requests'] })
      void client.invalidateQueries({ queryKey: ['dashboard-requests-stats'] })
      void client.invalidateQueries({ queryKey: [RequestDetailsQueryKey] })
      void client.invalidateQueries({ queryKey: taskQueryKeys.all })
    },
    onError: () => {
      enqueueSnackbar(t('pause.errors.resume-toast'), { variant: 'error' })
    },
  })
}
